import { buildingTint } from './skins';
import { loadSettings } from '../meta/settings';
import Phaser from 'phaser';
import type { Building, BuildingStore, BuildingEvent } from '../sim/buildings/BuildingStore';
import { buildingDef, type BuildingTypeId } from '../sim/buildings/definitions';
import { footprintCenter, footprintOutline, frontTile } from '../sim/buildings/footprint';
import type { PlacementResult } from '../sim/buildings/placement';
import type { IsoProjection } from '../core/iso';
import type { GridPoint } from '../core/types';
import type { BuildingTextureInfo } from './buildingArt';
import { LAYER, objectDepth } from './depth';
import { OWNER_COLORS, PLACEMENT_COLORS } from './palette';
import { ART_SCALE } from './proceduralArt';
import { FX_PUFF_KEY, type DamageNumbers, type Flashes } from './combatFx';

/** Visual damage state (Phase 6): intact, damaged (< 66 %: smoke), critical (< 33 %: fire and heavy smoke). */
export type DamageState = 'intact' | 'damaged' | 'critical';

export function damageState(health: number, maxHealth: number): DamageState {
  const f = maxHealth > 0 ? health / maxHealth : 1;
  return f < 1 / 3 ? 'critical' : f < 2 / 3 ? 'damaged' : 'intact';
}

const DAMAGE_TINT: Record<DamageState, number> = { intact: 0xffffff, damaged: 0xd9cbbd, critical: 0xa8907e };

interface BuildingView {
  building: Building;
  state: DamageState;
  /** Smoke (damaged) or smoke + fire (critical) emitters, created on demand. */
  smoke: Phaser.GameObjects.Particles.ParticleEmitter | null;
  fire: Phaser.GameObjects.Particles.ParticleEmitter | null;
  flashUntil: number;
  /** "⬆" while upgrading, "★" at level 2 (Phase 8). */
  badge: Phaser.GameObjects.Text | null;
  readonly sprite: Phaser.GameObjects.Image;
  /** Faint full-size silhouette shown while under construction. */
  readonly ghost: Phaser.GameObjects.Image;
  readonly bar: Phaser.GameObjects.Graphics;
  /** Owner-coloured ring on the ground around the footprint. */
  readonly ring: Phaser.GameObjects.Graphics;
}

/** Depth bias so a building draws in front of decorations rooted on the same front tile. */
const BUILDING_BIAS = 50;

/**
 * Draws placed buildings and the placement preview. Holds no gameplay state:
 * it mirrors BuildingRegistry events, so it can be rebuilt at any time.
 *
 * Buildings are not chunk-culled: Phase 2 has tens of them, not thousands.
 */
export class BuildingRenderer {
  private readonly views = new Map<number, BuildingView>();
  private readonly previewTiles: Phaser.GameObjects.Graphics;
  private readonly previewArea: Phaser.GameObjects.Graphics;
  private readonly previewGhost: Phaser.GameObjects.Image;
  private previewType: BuildingTypeId | null = null;
  /** Floating "+5" labels in flight (destroyed when their tween ends or on teardown). */
  private readonly floaters = new Set<Phaser.GameObjects.Text>();
  /** Destruction bursts still playing. */
  private readonly bursts = new Set<Phaser.GameObjects.Particles.ParticleEmitter>();
  private readonly unsubscribe: () => void;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly iso: IsoProjection,
    store: BuildingStore,
    private readonly art: ReadonlyMap<BuildingTypeId, BuildingTextureInfo>,
    /** Hide / show decorations on tiles a building covers. */
    private readonly setTilesCovered: (tiles: GridPoint[], covered: boolean) => void,
    private readonly flashes: Flashes,
    private readonly numbers: DamageNumbers,
  ) {
    this.previewArea = scene.add.graphics().setDepth(LAYER.overlay - 3);
    this.previewTiles = scene.add.graphics().setDepth(LAYER.overlay - 2);
    this.previewGhost = scene.add.image(0, 0, this.art.values().next().value!.key).setVisible(false).setDepth(LAYER.overlay - 1).setScale(1 / ART_SCALE);
    for (const b of store.all()) this.add(b);
    this.unsubscribe = store.subscribe((e) => this.onEvent(e));
  }

  get spriteCount(): number {
    return this.views.size;
  }

  /** World-space bounds of a building's rendered sprite (for alignment tests). */
  spriteBounds(id: number): Phaser.Geom.Rectangle | null {
    const v = this.views.get(id);
    return v ? v.sprite.getBounds() : null;
  }

  /** World point the sprite is anchored to (footprint centre on the ground). */
  spriteAnchor(id: number): { x: number; y: number } | null {
    const v = this.views.get(id);
    return v ? { x: v.sprite.x, y: v.sprite.y } : null;
  }

  /** World-space bounds of the placement preview ghost, or null when hidden. */
  previewBounds(): Phaser.Geom.Rectangle | null {
    return this.previewGhost.visible ? this.previewGhost.getBounds() : null;
  }

  /** Show the placement preview for a validation result, or hide it (null). */
  setPreview(p: PlacementResult | null): void {
    this.previewTiles.clear();
    this.previewArea.clear();
    if (!p) {
      this.previewGhost.setVisible(false);
      this.previewType = null;
      return;
    }
    const def = p.definition;
    const tex = this.art.get(def.id)!;
    if (this.previewType !== def.id) {
      this.previewGhost.setTexture(tex.key).setOrigin(tex.originX, tex.originY);
      this.previewType = def.id;
    }
    const c = footprintCenter(p.origin, def.footprint);
    const w = this.iso.gridToWorld(c.x, c.y);
    const color = p.ok ? PLACEMENT_COLORS.valid : PLACEMENT_COLORS.invalid;
    this.previewGhost.setPosition(w.x, w.y).setVisible(true).setAlpha(0.72).setTint(p.ok ? 0xd9ffd9 : 0xff9a8f);

    // Area the building-specific rule inspects (e.g. sawmill forest radius).
    const rule = def.requiresNearby;
    if (rule) {
      const ring = footprintOutline({ x: p.origin.x - rule.radius, y: p.origin.y - rule.radius }, { width: def.footprint.width + rule.radius * 2, height: def.footprint.height + rule.radius * 2 });
      this.previewArea.lineStyle(1.5, p.failures.includes('requires_nearby') ? PLACEMENT_COLORS.invalid : 0xe8e2c8, 0.75);
      this.previewArea.strokePoints(ring.map((g) => this.iso.gridToWorld(g.x, g.y)) as unknown as Phaser.Math.Vector2[], true, true);
    }

    // Per-tile validity: green tiles fine, red tiles are the problem.
    for (const t of p.tiles) {
      const pts = this.iso.tileCorners(t.x, t.y) as unknown as Phaser.Math.Vector2[];
      const tc = t.ok && p.ok ? PLACEMENT_COLORS.valid : t.ok ? 0xf0b43c : PLACEMENT_COLORS.invalid;
      this.previewTiles.fillStyle(tc, 0.38);
      this.previewTiles.fillPoints(pts, true);
    }
    const outline = footprintOutline(p.origin, def.footprint).map((g) => this.iso.gridToWorld(g.x, g.y));
    this.previewTiles.lineStyle(2.5, color, 1);
    this.previewTiles.strokePoints(outline as unknown as Phaser.Math.Vector2[], true, true);
  }

  /**
   * Short floating label above a building ("+5", "Storage full"): rises and
   * fades in ~1.2 s. Purely cosmetic; capped so a burst can never pile up.
   */
  floatText(id: number, text: string, color: string): void {
    const v = this.views.get(id);
    if (!v || this.floaters.size > 40) return;
    const def = buildingDef(v.building.type);
    const c = footprintCenter(v.building.origin, def.footprint);
    const w = this.iso.gridToWorld(c.x, c.y);
    const rise = this.art.get(v.building.type)?.rise ?? this.iso.tileHeight;
    const t = this.scene.add
      .text(w.x, w.y - rise - 6, text, {
        fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif",
        fontStyle: '700',
        fontSize: '18px',
        color,
        stroke: '#101214',
        strokeThickness: 4,
        resolution: 2,
      })
      .setOrigin(0.5, 1)
      .setDepth(LAYER.overlay + 5);
    this.floaters.add(t);
    this.scene.tweens.add({
      targets: t,
      y: t.y - 26,
      alpha: { from: 1, to: 0 },
      ease: 'Cubic.easeOut',
      duration: 1200,
      onComplete: () => {
        this.floaters.delete(t);
        t.destroy();
      },
    });
  }

  get floaterCount(): number {
    return this.floaters.size;
  }

  /** Damage state of a building as drawn (for tests). */
  damageStateOf(id: number): DamageState | null {
    return this.views.get(id)?.state ?? null;
  }

  get burstCount(): number {
    return this.bursts.size;
  }

  /** Per-frame: end hit flashes. */
  update(now: number): void {
    for (const v of this.views.values()) {
      if (v.flashUntil && v.flashUntil <= now) {
        v.flashUntil = 0;
        this.applyTint(v);
      }
    }
  }

  destroy(): void {
    this.unsubscribe();
    for (const t of this.floaters) t.destroy();
    this.floaters.clear();
    for (const id of [...this.views.keys()]) this.removeView(id);
    for (const b of this.bursts) b.destroy();
    this.bursts.clear();
    this.previewTiles.destroy();
    this.previewArea.destroy();
    this.previewGhost.destroy();
  }

  // ------------------------------------------------------------------

  private onEvent(e: BuildingEvent): void {
    switch (e.type) {
      case 'added':
        this.add(e.building);
        break;
      case 'removed':
        if (e.destroyed) this.explode(e.building);
        this.removeView(e.building.id);
        this.setTilesCovered(this.tilesOf(e.building), false);
        break;
      case 'damaged': {
        const v = this.views.get(e.building.id);
        if (v) {
          v.flashUntil = this.scene.time.now + 70;
          v.sprite.setTintFill(0xfff0e0);
          const top = this.topOf(e.building);
          this.numbers.add(`b${e.building.id}`, top.x, top.y - 8, e.amount, '#ffb08f');
        }
        this.updateConstruction(e.building);
        break;
      }
      case 'progress':
      case 'completed':
        this.updateConstruction(e.building);
        break;
      case 'upgradeStarted':
      case 'upgraded':
      case 'repaired':
        this.updateConstruction(e.building);
        this.updateBadge(e.building);
        break;
    }
  }

  private add(b: Building): void {
    const def = buildingDef(b.type);
    const tex = this.art.get(b.type)!;
    const c = footprintCenter(b.origin, def.footprint);
    const w = this.iso.gridToWorld(c.x, c.y);
    const front = frontTile(b.origin, def.footprint);
    const depth = objectDepth(this.iso, front.x, front.y, BUILDING_BIAS);
    const ghost = this.scene.add.image(w.x, w.y, tex.key).setOrigin(tex.originX, tex.originY).setScale(1 / ART_SCALE).setDepth(depth - 0.0001).setAlpha(0.22).setTint(0xb8c4c8);
    const sprite = this.scene.add.image(w.x, w.y, tex.key).setOrigin(tex.originX, tex.originY).setScale(1 / ART_SCALE).setDepth(depth);
    const bar = this.scene.add.graphics().setDepth(LAYER.overlay - 10);
    const ring = this.scene.add.graphics().setDepth(LAYER.markers - 1);
    const oc = OWNER_COLORS[b.ownerId] ?? OWNER_COLORS.neutral!;
    const outline = footprintOutline(b.origin, def.footprint).map((g) => this.iso.gridToWorld(g.x, g.y)) as unknown as Phaser.Math.Vector2[];
    ring.fillStyle(oc.color, 0.18);
    ring.fillPoints(outline, true);
    ring.lineStyle(2, oc.color, 0.9);
    ring.strokePoints(outline, true, true);
    const view: BuildingView = { building: b, state: 'intact', smoke: null, fire: null, flashUntil: 0, badge: null, sprite, ghost, bar, ring };
    this.views.set(b.id, view);
    this.setTilesCovered(this.tilesOf(b), true);
    this.updateConstruction(b);
    this.applyTint(view);
  }

  private updateConstruction(b: Building): void {
    const v = this.views.get(b.id);
    if (!v) return;
    v.building = b;
    this.setDamageState(v, damageState(b.health, b.maxHealth));
    const done = b.state === 'complete';
    v.ghost.setVisible(!done);
    v.bar.clear();
    if (done) {
      v.sprite.setCrop();
      if (b.health < b.maxHealth) this.drawBar(v.bar, b, b.health / b.maxHealth, healthColor(b.health / b.maxHealth));
      return;
    }
    // Rise from the ground: reveal the sprite bottom-up with progress.
    const f = v.sprite.frame;
    const shown = 0.12 + 0.88 * b.progress;
    v.sprite.setCrop(0, f.height * (1 - shown), f.width, f.height * shown);

    this.drawBar(v.bar, b, b.progress, 0xf0b43c);
  }

  /** A small bar above the footprint (construction progress or, once built, health). */
  private drawBar(g: Phaser.GameObjects.Graphics, b: Building, frac: number, color: number): void {
    const def = buildingDef(b.type);
    const top = this.iso.tileCorners(b.origin.x, b.origin.y)[0];
    const c = footprintCenter(b.origin, def.footprint);
    const cx = this.iso.gridToWorld(c.x, c.y).x;
    const tex = this.art.get(b.type)!;
    const y = top.y - tex.rise - 6;
    const width = Math.max(28, this.iso.tileWidth * 0.8);
    g.fillStyle(0x111316, 0.85);
    g.fillRect(cx - width / 2 - 1, y - 1, width + 2, 6);
    g.fillStyle(color, 1);
    g.fillRect(cx - width / 2, y, width * Math.max(0, Math.min(1, frac)), 4);
  }

  private updateBadge(b: Building): void {
    const v = this.views.get(b.id);
    if (!v) return;
    const text = b.upgradeProgress !== null ? '⬆' : b.level >= 2 ? '★' : '';
    if (!text) {
      v.badge?.destroy();
      v.badge = null;
      return;
    }
    const top = this.topOf(b);
    if (!v.badge) {
      v.badge = this.scene.add
        .text(top.x, top.y - 2, text, { fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif", fontStyle: '700', fontSize: '14px', color: '#f0b43c', stroke: '#101214', strokeThickness: 3, resolution: 2 })
        .setOrigin(0.5, 1)
        .setDepth(LAYER.overlay - 9);
    } else v.badge.setText(text);
  }

  private skin: { owner: string; id: string | null } = { owner: '', id: null };

  /** Cosmetic skin for one owner's buildings (D122); re-tints what is on the map. */
  setSkin(owner: string, id: string | null): void {
    this.skin = { owner, id };
    for (const v of this.views.values()) this.applyTint(v);
  }

  private applyTint(v: BuildingView): void {
    if (v.flashUntil) return;
    if (v.state === 'intact') {
      const t = v.building.ownerId === this.skin.owner ? buildingTint(this.skin.id, v.building.type) : null;
      if (t === null) v.sprite.clearTint();
      else v.sprite.setTint(t);
    } else v.sprite.setTint(DAMAGE_TINT[v.state]);
  }

  /** Screen-space (world px) point just above the building's art. */
  private topOf(b: Building): { x: number; y: number } {
    const def = buildingDef(b.type);
    const c = footprintCenter(b.origin, def.footprint);
    const w = this.iso.gridToWorld(c.x, c.y);
    return { x: w.x, y: w.y - (this.art.get(b.type)?.rise ?? this.iso.tileHeight) };
  }

  private setDamageState(v: BuildingView, state: DamageState): void {
    if (state === v.state) return;
    v.state = state;
    this.applyTint(v);
    const top = this.topOf(v.building);
    const depth = v.sprite.depth + 0.0002;
    if (state === 'intact') {
      v.smoke?.destroy();
      v.fire?.destroy();
      v.smoke = v.fire = null;
      return;
    }
    const heavy = state === 'critical';
    if (loadSettings().quality === 'low') return; // Low graphics: the damage tint stays, no particles
    v.smoke?.destroy();
    v.smoke = this.scene.add
      .particles(top.x, top.y + 6, FX_PUFF_KEY, {
        x: { min: -10, max: 10 },
        speedY: { min: -26, max: -14 },
        speedX: { min: -4, max: 6 },
        lifespan: heavy ? 1700 : 1400,
        scale: { start: 0.35, end: heavy ? 1.3 : 1 },
        alpha: { start: heavy ? 0.55 : 0.4, end: 0 },
        tint: heavy ? 0x3a3532 : 0x6d6862,
        frequency: heavy ? 160 : 320,
      })
      .setDepth(depth + 0.0001);
    if (heavy && !v.fire) {
      v.fire = this.scene.add
        .particles(top.x, top.y + 10, FX_PUFF_KEY, {
          x: { min: -8, max: 8 },
          speedY: { min: -22, max: -10 },
          lifespan: 520,
          scale: { start: 0.45, end: 0.1 },
          alpha: { start: 0.9, end: 0 },
          tint: [0xffd16a, 0xff8a3a, 0xe8502a],
          blendMode: 'ADD',
          frequency: 70,
        })
        .setDepth(depth);
    } else if (!heavy) {
      v.fire?.destroy();
      v.fire = null;
    }
  }

  /** Destruction: a burst of fire and dust where the building stood (cosmetic, ~1.5 s). */
  private explode(b: Building): void {
    const def = buildingDef(b.type);
    const c = footprintCenter(b.origin, def.footprint);
    const w = this.iso.gridToWorld(c.x, c.y);
    const size = Math.max(def.footprint.width, def.footprint.height);
    const burst = this.scene.add
      .particles(w.x, w.y - 10, FX_PUFF_KEY, {
        x: { min: -10 * size, max: 10 * size },
        y: { min: -6 * size, max: 4 },
        speed: { min: 20, max: 70 },
        angle: { min: 200, max: 340 },
        lifespan: { min: 600, max: 1300 },
        scale: { start: 0.9, end: 2 },
        alpha: { start: 0.8, end: 0 },
        tint: [0x58514b, 0x7a7068, 0xff9b45, 0xffcf6a],
        emitting: false,
      })
      .setDepth(LAYER.overlay - 12);
    burst.explode(14 + size * 6);
    this.flashes.ring(w.x, w.y - 8, 12 * size, 0xffb35c, 320);
    this.bursts.add(burst);
    this.scene.time.delayedCall(1500, () => {
      this.bursts.delete(burst);
      burst.destroy();
    });
  }

  private removeView(id: number): void {
    const v = this.views.get(id);
    if (!v) return;
    v.smoke?.destroy();
    v.fire?.destroy();
    v.badge?.destroy();
    v.sprite.destroy();
    v.ghost.destroy();
    v.bar.destroy();
    v.ring.destroy();
    this.views.delete(id);
  }

  private tilesOf(b: Building): GridPoint[] {
    const fp = buildingDef(b.type).footprint;
    const out: GridPoint[] = [];
    for (let y = 0; y < fp.height; y++) for (let x = 0; x < fp.width; x++) out.push({ x: b.origin.x + x, y: b.origin.y + y });
    return out;
  }
}

/** Green → amber → red as health drops. */
export function healthColor(frac: number): number {
  return frac > 0.6 ? 0x6fcf5a : frac > 0.3 ? 0xf0b43c : 0xef5a4c;
}
